import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/** Private line item property that marks the order-level gift-wrap line. */
const GIFT_WRAP_PROPERTY = '_cc_cart_gift_wrap';

/**
 * Order-level gift wrap checkbox rendered by `snippets/cc-cart-gift-wrap.liquid`.
 *
 * - With `data-variant-id`: toggling adds/removes the gift-wrap product line, then hands the
 *   returned section HTML to `cart-items-component` through `CartLinesUpdateEvent`.
 * - Without it: toggling sets/clears the `data-property-name` line item property on every
 *   product line in the cart. The property name is separate from `_cc-gift-wrap-item`'s
 *   label, so product page gift-wrap tags are never read or cleared here.
 *
 * @typedef {{ checkbox: HTMLInputElement }} Refs
 * @extends {Component<Refs>}
 */
class CartGiftWrap extends Component {
  requiredRefs = ['checkbox'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
  }

  /**
   * Handles the checkbox change. The framework re-targets the event to the `on:change`
   * wrapper, so the state is read from the checkbox ref instead of `event.target`.
   */
  toggle = async () => {
    const { checkbox } = this.refs;
    if (!checkbox) return;

    const checked = checkbox.checked;

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    this.setAttribute('aria-busy', 'true');

    try {
      if (this.dataset.variantId) {
        await this.#toggleProduct(checked, abortController.signal);
      } else {
        await this.#toggleLineProperty(checked, abortController.signal);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      // Revert so the checkbox keeps matching the cart.
      this.refs.checkbox.checked = !checked;
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Failed to update gift wrap',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
        this.removeAttribute('aria-busy');
      }
    }
  };

  /**
   * Sets `properties[name]` to `Yes` (or blank) on every cart line. Each line's existing
   * properties are sent along so nothing else on the line changes.
   * Changing properties only re-keys the line being changed, so the other keys stay valid.
   * @param {boolean} checked
   * @param {AbortSignal} signal
   */
  async #toggleLineProperty(checked, signal) {
    const propertyName = this.dataset.propertyName;
    if (!propertyName) return;

    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { signal });
    if (!cartResponse.ok) throw new Error(`Failed to fetch cart: ${cartResponse.status}`);

    /** @type {{ items: Array<{ key: string, quantity: number, properties: Record<string, string> | null }> }} */
    const cart = await cartResponse.json();
    const lines = cart.items.filter((item) => {
      const hasProperty = item.properties?.[propertyName] === 'Yes';
      return checked ? !hasProperty : hasProperty;
    });
    if (lines.length === 0) return;

    const sectionIds = this.#sectionIds();
    const deferredPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: lines.map((line) => ({ id: line.key, quantity: line.quantity })),
        promise: deferredPromise.promise,
      })
    );

    try {
      let result;

      for (const [index, line] of lines.entries()) {
        // `/cart/change` keeps a property that is simply omitted, so unchecking blanks it instead.
        // Blank properties are hidden in the cart (`cart-products.liquid`) and at checkout.
        const properties = { ...(line.properties ?? {}), [propertyName]: checked ? 'Yes' : '' };

        const isLast = index === lines.length - 1;
        const response = await fetch(Theme.routes.cart_change_url, {
          ...fetchConfig('json', {
            body: JSON.stringify({
              id: line.key,
              quantity: line.quantity,
              properties,
              ...(isLast && { sections: sectionIds.join(','), sections_url: window.location.pathname }),
            }),
          }),
          signal,
        });

        result = await response.json();
        if (result.status || result.errors) {
          throw new Error(result.description || result.errors || result.message);
        }
      }

      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
        detail: {
          items: result.items,
          itemCount: result.item_count,
          sections: result.sections,
          source: 'cc-cart-gift-wrap',
          didError: false,
        },
      });
    } catch (error) {
      deferredPromise.reject(error);
      throw error;
    }
  }

  /**
   * Checking adds the gift-wrap variant as its own marked line. Unchecking looks the cart up and
   * removes every line carrying the marker, rather than trusting a line key rendered earlier
   * that may be stale after other cart changes.
   * @param {boolean} checked
   * @param {AbortSignal} signal
   */
  async #toggleProduct(checked, signal) {
    const variantId = this.dataset.variantId;
    const sectionIds = this.#sectionIds();

    /** @type {string[]} */
    let giftWrapKeys = [];
    if (!checked) {
      const currentCart = await this.#fetchCart(signal);
      giftWrapKeys = currentCart.items
        .filter((item) => item.properties?.[GIFT_WRAP_PROPERTY])
        .map((item) => item.key);
      if (giftWrapKeys.length === 0) return;
    }

    const deferredPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      checked
        ? new CartLinesUpdateEvent({
            action: 'add',
            context: 'cart',
            lines: [{ merchandiseId: String(variantId), quantity: 1 }],
            promise: deferredPromise.promise,
          })
        : new CartLinesUpdateEvent({
            action: 'remove',
            context: 'cart',
            lines: giftWrapKeys.map((key) => ({ id: key, quantity: 0 })),
            promise: deferredPromise.promise,
          })
    );

    try {
      const sectionsParams = { sections: sectionIds.join(','), sections_url: window.location.pathname };
      let sections;

      if (checked) {
        const addResponse = await fetch(Theme.routes.cart_add_url, {
          ...fetchConfig('json', {
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1, properties: { [GIFT_WRAP_PROPERTY]: 'true' } }],
              ...sectionsParams,
            }),
          }),
          signal,
        });
        const addResult = await addResponse.json();
        if (addResult.status) throw new Error(addResult.description || addResult.message);
        sections = addResult.sections;
      } else {
        const updates = Object.fromEntries(giftWrapKeys.map((key) => [key, 0]));
        const updateResponse = await fetch(Theme.routes.cart_update_url, {
          ...fetchConfig('json', { body: JSON.stringify({ updates, ...sectionsParams }) }),
          signal,
        });
        const updateResult = await updateResponse.json();
        if (updateResult.status || updateResult.errors) {
          throw new Error(updateResult.description || updateResult.errors || updateResult.message);
        }
        sections = updateResult.sections;
      }

      const cart = await this.#fetchCart(signal);

      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections,
          source: 'cc-cart-gift-wrap',
          didError: false,
        },
      });
    } catch (error) {
      deferredPromise.reject(error);
      throw error;
    }
  }

  /**
   * @param {AbortSignal} signal
   * @returns {Promise<{ item_count: number, items: Array<{ key: string, properties: Record<string, string> | null }> }>}
   */
  async #fetchCart(signal) {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { signal });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** Every cart-items-component section on the page, so drawer and cart page both refresh. */
  #sectionIds() {
    const ids = new Set();
    if (this.dataset.sectionId) ids.add(this.dataset.sectionId);
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
    }
    return Array.from(ids);
  }
}

if (!customElements.get('cc-cart-gift-wrap')) {
  customElements.define('cc-cart-gift-wrap', CartGiftWrap);
}
